import { useState } from 'react';
import { toast } from 'sonner';
import { Captions, Lock, SlidersHorizontal, Volume2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Slider } from '../../ui/slider';
import { Switch } from '../../ui/switch';
import { errMsg, mtvSettings } from '../../../lib/aiStudioApi';
import { hasStems } from './mtvSteps';

const Row = ({ icon: Icon, title, sub, children, testId }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-900 py-3 last:border-0" data-testid={testId}>
    <div className="flex min-w-0 items-start gap-2.5"><Icon size={14} className="mt-0.5 shrink-0 text-amber-300" />
      <div><div className="text-xs font-medium text-neutral-100">{title}</div><div className="text-[11px] text-neutral-500">{sub}</div></div></div>
    <div className="min-w-[180px]">{children}</div>
  </div>
);

/** Music-video mixer: the song is locked at 100%; clip sound is off by default; captions follow the song clock. */
export function MtvMixCard({ project, onChanged, onGoToStep }) {
  const mtv = project.mtv || {};
  const [clip, setClip] = useState(mtv.clip_audio || 0);
  const save = (patch, ok) => mtvSettings(project.id, patch).then(() => { if (ok) toast.success(ok); onChanged(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="rounded-xl border border-neutral-800 bg-neutral-900/40 px-5 py-2" data-testid="mtv-mix-card">
      <Row icon={Lock} title="Song" sub="The soundtrack — always at full volume" testId="mtv-mix-song"><span className="font-mono text-xs text-amber-200">100% · locked</span></Row>
      <Row icon={Volume2} title="Clip sound" sub="Some shots carry their own sound — off unless you raise it" testId="mtv-mix-clip">
        <div className="flex items-center gap-2">
          <Slider min={0} max={100} step={5} value={[clip]} onValueChange={([v]) => setClip(v)} onValueCommit={([v]) => save({ clip_audio: v })} data-testid="mtv-clip-audio-slider" />
          <span className="w-9 text-right font-mono text-[11px] text-neutral-300" data-testid="mtv-clip-audio-value">{clip}%</span>
        </div>
      </Row>
      <Row icon={SlidersHorizontal} title="Vocals & music balance" sub={hasStems(project) ? 'Make a custom vocal/music mix, then pick it as the soundtrack' : 'Split the vocals first to rebalance them'} testId="mtv-mix-stems">
        <Button size="sm" variant="outline" onClick={() => onGoToStep(2)} className="h-7 border-neutral-700 text-[11px] text-neutral-200" data-testid="mtv-mix-open-sound">Open Sound</Button>
      </Row>
      <Row icon={Captions} title="Lyric captions" sub="Each lyric line appears on screen while it is sung" testId="mtv-mix-captions">
        <Switch checked={!!mtv.captions} onCheckedChange={(v) => save({ captions: v }, v ? 'Lyric captions on' : 'Lyric captions off')} data-testid="mtv-captions-switch" />
      </Row>
    </div>
  );
}
